import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Calendar, User, PlayCircle, Users } from "lucide-react";
import { api } from "../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" }); } catch { return ""; } };

function Card({ w, replay }) {
  return (
    <Link to={`/webinaire/${w.id}`} data-testid={`webinar-card-${w.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-lg">
      <div className="relative h-40 bg-navy">
        {w.cover_url ? <img src={w.cover_url} alt={w.title} className="h-full w-full object-cover" />
          : <div className="flex h-full items-center justify-center bg-gradient-to-br from-navy to-sky-800"><Radio className="text-white/40" size={40} /></div>}
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold ${w.price_xof === 0 ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"}`}>{w.price_label}</span>
        {w.live && <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow" data-testid={`webinar-live-badge-${w.id}`}><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> EN DIRECT</span>}
        {replay && <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-[11px] font-semibold text-navy"><PlayCircle size={12} /> Replay</span>}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-base font-semibold text-navy group-hover:text-ciel">{w.title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-slate-500">{w.description}</p>
        <div className="mt-3 space-y-1 text-xs text-slate-500">
          {w.speaker && <p className="flex items-center gap-1.5"><User size={13} /> {w.speaker}{w.speaker_title ? ` · ${w.speaker_title}` : ""}</p>}
          <p className="flex items-center gap-1.5"><Calendar size={13} /> {fmt(w.starts_at)}</p>
          {!replay && w.remaining !== null && <p className="flex items-center gap-1.5"><Users size={13} /> {w.is_full ? "Complet" : `${w.remaining} place(s) restante(s)`}</p>}
        </div>
        <span className="mt-4 inline-block rounded-full bg-ciel px-4 py-2 text-center text-sm font-semibold text-white group-hover:bg-sky-600">
          {replay ? "Voir le replay" : "S'inscrire"}
        </span>
      </div>
    </Link>
  );
}

export default function Webinaires() {
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  useEffect(() => {
    api.get("/webinars?scope=upcoming").then((r) => setUpcoming(r.data.webinars)).catch(() => {});
    api.get("/webinars?scope=past").then((r) => setPast(r.data.webinars.filter((w) => w.replay_available))).catch(() => {});
  }, []);
  return (
    <div className="mx-auto max-w-6xl px-4 py-12" data-testid="webinaires-page">
      <div className="mb-10 max-w-2xl">
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-ciel"><Radio size={16} /> Webinaires Cabinet CARIS Santé</p>
        <h1 className="font-display text-4xl font-bold text-navy sm:text-5xl">Webinaires santé & médecine</h1>
        <p className="mt-3 text-base text-slate-500">Conférences en ligne animées par des médecins et professionnels de santé. Inscrivez-vous gratuitement ou accédez aux sessions payantes en FCFA.</p>
      </div>

      <h2 className="mb-4 font-display text-lg font-semibold text-navy">Prochains webinaires</h2>
      {upcoming.length === 0 ? <p className="mb-12 text-sm text-slate-400">Aucun webinaire programmé pour le moment.</p> : (
        <div className="mb-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="webinaires-upcoming">
          {upcoming.map((w) => <Card key={w.id} w={w} />)}
        </div>
      )}

      {past.length > 0 && (
        <>
          <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-semibold text-navy"><PlayCircle size={18} className="text-ciel" /> Rediffusions (replay)</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="webinaires-replays">
            {past.map((w) => <Card key={w.id} w={w} replay />)}
          </div>
        </>
      )}
    </div>
  );
}
